<template>
    <div class="compass-card accent-top">
        <h3 class="h5 text-gradient mb-3">Create Limit Order</h3>
        <p class="text-muted small mb-3">
            Set a target price and your order executes automatically when the market reaches it.
        </p>

        <!-- From Token -->
        <div class="mb-3">
            <label class="form-label small text-muted">Sell Token</label>
            <div class="dca-token-picker" @click="openSelector('from')">
                <img v-if="fromToken.logoURI" :src="fromToken.logoURI" class="dca-token-logo" alt="">
                <span class="dca-token-symbol">{{ fromToken.symbol || 'Select token' }}</span>
                <svg class="dca-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
        </div>

        <!-- To Token -->
        <div class="mb-3">
            <label class="form-label small text-muted">Buy Token</label>
            <div class="dca-token-picker" @click="openSelector('to')">
                <img v-if="toToken.logoURI" :src="toToken.logoURI" class="dca-token-logo" alt="">
                <span class="dca-token-symbol">{{ toToken.symbol || 'Select token' }}</span>
                <svg class="dca-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
        </div>

        <!-- Token Selector Modal -->
        <TokenSelectorModal
            v-model="showTokenSelector"
            :mode="tokenSelectorMode"
            :current-from-token="fromToken.mint"
            :current-to-token="toToken.mint"
            @select="handleTokenSelect"
        />

        <!-- Amount -->
        <div class="mb-3">
            <label class="form-label small text-muted">Amount to Sell</label>
            <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    v-model.number="inputAmount"
                    placeholder="0.00"
                    min="0"
                    step="any"
                >
                <span class="input-group-text" v-if="fromToken">{{ fromToken.symbol || 'Token' }}</span>
            </div>
        </div>

        <!-- Target Price -->
        <div class="mb-3">
            <label class="form-label small text-muted">Target Price ({{ toToken?.symbol || 'Token' }} per {{ fromToken?.symbol || 'Token' }})</label>
            <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    v-model.number="targetPrice"
                    placeholder="0.00"
                    min="0"
                    step="any"
                >
                <span class="input-group-text">{{ toToken?.symbol || '' }}/{{ fromToken?.symbol || '' }}</span>
            </div>
        </div>

        <!-- Expiry -->
        <div class="mb-3">
            <label class="form-label small text-muted">Expiry</label>
            <div class="d-flex gap-2">
                <button
                    v-for="opt in expiryOptions"
                    :key="opt.value"
                    class="compass-btn compass-btn--sm flex-fill"
                    :class="expiry === opt.value ? 'compass-btn--primary' : 'compass-btn--outline'"
                    @click="expiry = opt.value"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>

        <!-- Summary -->
        <div class="compass-info-box mb-3" v-if="inputAmount > 0 && targetPrice > 0">
            <div class="d-flex justify-content-between mb-1">
                <span class="text-muted small">You Sell</span>
                <span class="mono-num small">{{ formatAmount(inputAmount) }} {{ fromToken?.symbol }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1">
                <span class="text-muted small">You Receive (est.)</span>
                <span class="mono-num small">{{ formatAmount(estimatedOutput) }} {{ toToken?.symbol }}</span>
            </div>
            <div class="d-flex justify-content-between">
                <span class="text-muted small">Target Price</span>
                <span class="mono-num small">{{ formatAmount(targetPrice) }} {{ toToken?.symbol }}/{{ fromToken?.symbol }}</span>
            </div>
        </div>

        <!-- Error -->
        <div class="alert alert-danger small py-2" v-if="error">
            {{ error }}
        </div>

        <!-- Success -->
        <div class="alert alert-success small py-2" v-if="successMessage">
            {{ successMessage }}
            <a v-if="explorerUrl" :href="explorerUrl" target="_blank" class="ms-1">View tx <i class="fas fa-external-link-alt"></i></a>
        </div>

        <!-- Submit -->
        <button
            class="compass-btn compass-btn--primary w-100"
            :disabled="!canSubmit || isSubmitting"
            @click="createLimit"
        >
            <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin me-1"></i> Creating Limit Order...
            </span>
            <span v-else-if="!isConnected">
                Connect Wallet
            </span>
            <span v-else>
                Place Limit Order
            </span>
        </button>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';
import TokenSelectorModal from './TokenSelectorModal.vue';

export default {
    name: 'LimitOrders',

    components: { TokenSelectorModal },

    props: {
        inputToken: { type: String, default: '' },
        outputToken: { type: String, default: '' },
    },

    data() {
        return {
            fromToken: this.inputToken ? { symbol: '', mint: this.inputToken } : { symbol: 'SOL', mint: 'So11111111111111111111111111111111111111112' },
            toToken: this.outputToken ? { symbol: '', mint: this.outputToken } : { symbol: 'USDC', mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' },
            showTokenSelector: false,
            tokenSelectorMode: 'from',
            inputAmount: null,
            targetPrice: null,
            expiry: '7d',
            isSubmitting: false,
            error: null,
            successMessage: null,
            explorerUrl: null,
            expiryOptions: [
                { label: '1 Day', value: '1d' },
                { label: '7 Days', value: '7d' },
                { label: '30 Days', value: '30d' },
                { label: 'Never', value: 'never' },
            ],
        };
    },

    computed: {
        isConnected() {
            return !!window.wallet?.publicKey?.value;
        },

        estimatedOutput() {
            if (!this.inputAmount || !this.targetPrice) return 0;
            return this.inputAmount * this.targetPrice;
        },

        canSubmit() {
            return this.isConnected
                && this.fromToken?.symbol
                && this.toToken?.symbol
                && this.inputAmount > 0
                && this.targetPrice > 0
                && !this.isSubmitting;
        },

        expiryTimestamp() {
            if (this.expiry === 'never') return null;
            const ms = { '1d': 86400000, '7d': 604800000, '30d': 2592000000 };
            return Date.now() + (ms[this.expiry] || 604800000);
        },
    },

    methods: {
        openSelector(mode) {
            this.tokenSelectorMode = mode;
            this.showTokenSelector = true;
        },

        handleTokenSelect({ token, mode }) {
            const mapped = {
                symbol: token.symbol,
                mint: token.mint,
                logoURI: token.logoURI || token.logo || null,
                name: token.name,
            };
            if (mode === 'from') {
                this.fromToken = mapped;
            } else {
                this.toToken = mapped;
            }
        },

        formatAmount(val) {
            if (val >= 1) return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
            return val.toPrecision(4);
        },

        async createLimit() {
            this.error = null;
            this.successMessage = null;
            this.explorerUrl = null;
            this.isSubmitting = true;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();

                const opts = {
                    targetPrice: this.targetPrice,
                    slippageBps: 50,
                };
                if (this.expiryTimestamp) {
                    opts.expiredAt = Math.floor(this.expiryTimestamp / 1000);
                }

                const result = await sdk.order.createLimit(
                    this.inputAmount,
                    this.fromToken.symbol,
                    this.toToken.symbol,
                    'default',
                    opts
                );

                this.successMessage = `Limit order placed! Sell ${this.formatAmount(result.inputAmount)} ${result.inputSymbol} at ${this.formatAmount(result.targetPrice)} ${result.outputSymbol}/${result.inputSymbol}`;
                this.explorerUrl = result.signature ? `https://solscan.io/tx/${result.signature}` : null;
                this.$emit('order-created', result);
            } catch (e) {
                this.error = e.message || 'Failed to create limit order';
                console.error('[LimitOrders] createLimit error:', e);
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.dca-token-picker {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1rem;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.2s;
}
.dca-token-picker:hover {
    border-color: rgba(139, 92, 246, 0.5);
}
.dca-token-logo {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}
.dca-token-symbol {
    font-weight: 600;
    flex: 1;
}
.dca-chevron {
    opacity: 0.5;
}
</style>
